import React, { useEffect, useState, useMemo } from "react";
import axiosInstance from "../../../../services/axiosInstance";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import {
  Box,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  ThemeProvider,
  createTheme,
  CssBaseline,
  TablePagination,
  Button,
  Grid,
  IconButton,
  Portal,
  ClickAwayListener,
  Stack,
  TextField,
  InputAdornment,
  Tooltip,
  LinearProgress,
  Fade,
  Skeleton,
  Avatar,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Legend,
  CartesianGrid,
  RadialBarChart,
  RadialBar,
  AreaChart,
  Area,
} from "recharts";
import {
  PlayCircle,
  Search,
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Zap,
  Target,
  Phone,
  Download,
  FilterX,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import * as XLSX from "xlsx";
import CloseIcon from "@mui/icons-material/Close";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import TimerIcon from "@mui/icons-material/Timer";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SideNavbar from "../../../../components/sideNavbar";
import Header from "../../../../components/header";
import apiEndPointsConfig from "../../../../services/apiEndPoints";
import EmptyState from "../components/EmptyState";

const theme = createTheme({
  palette: {
    primary: { main: "#6366F1" },
    success: { main: "#10B981" },
    warning: { main: "#F59E0B" },
    error: { main: "#EF4444" },
    // background: { default: "#F8FAFC", paper: "#FFFFFF" },
  },
  typography: {
    fontFamily: "'Inter', 'Roboto', sans-serif",
  },
  shape: { borderRadius: 12 },
});

const COLORS = {
  onTime: "#10B981",
  late: "#F59E0B",
  missed: "#EF4444",
  primary: "#6366F1",
};

// Animated KPI Card Component
const KPICard = ({ title, value, subtitle, icon: Icon, color, loading, trend }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4 }}
  >
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 1,
        border: "1px solid #E2E8F0",
        transition: "all 0.3s ease",
        cursor: "pointer",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 12px 24px rgba(0,0,0,0.08)",
          borderColor: color,
        },
      }}
    >
      {loading ? (
        <Stack spacing={1}>
          <Skeleton width="60%" height={24} />
          <Skeleton width="40%" height={40} />
          <Skeleton width="80%" height={20} />
        </Stack>
      ) : (
        <Stack spacing={1}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography
              variant="caption"
              sx={{ color: "#64748B", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}
            >
              {title}
            </Typography>
            <Box
              sx={{
                p: 1,
                borderRadius: 1,
                bgcolor: `${color}15`,
                color: color,
                display: "flex",
              }}
            >
              <Icon size={18} />
            </Box>
          </Stack>
          <Typography variant="h5" sx={{ fontWeight: 800, color: "#1E293B" }}>
            {value}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            {trend && (
              <Chip
                size="small"
                icon={<TrendingUp size={12} />}
                label={trend}
                sx={{
                  bgcolor: `${COLORS.onTime}15`,
                  color: COLORS.onTime,
                  fontWeight: 600,
                  fontSize: "0.7rem",
                }}
              />
            )}
            <Typography variant="caption" sx={{ color: "#94A3B8" }}>
              {subtitle}
            </Typography>
          </Stack>
        </Stack>
      )}
    </Paper>
  </motion.div>
);

// Modern Gauge Chart for SLA Percentage
const SLAGauge = ({ percentage, loading }) => {
  const data = [{ name: "SLA", value: percentage, fill: COLORS.primary }];

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 1,
        border: "1px solid #E2E8F0",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#475569", mb: 2 }}>
        SLA Achievement Rate
      </Typography>
      {loading ? (
        <Skeleton variant="circular" width={140} height={140} sx={{ mx: "auto" }} />
      ) : (
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <ResponsiveContainer width="100%" height={160}>
            <RadialBarChart
              cx="50%"
              cy="50%"
              innerRadius="60%"
              outerRadius="90%"
              barSize={20}
              data={data}
              startAngle={180}
              endAngle={0}
            >
              <RadialBar
                background={{ fill: "#F1F5F9" }}
                dataKey="value"
                cornerRadius={10}
              />
              <text
                x="50%"
                y="50%"
                textAnchor="middle"
                dominantBaseline="middle"
                style={{ fontSize: "28px", fontWeight: 800, fill: COLORS.primary }}
              >
                {percentage}%
              </text>
              <text
                x="50%"
                y="62%"
                textAnchor="middle"
                style={{ fontSize: "12px", fill: "#94A3B8" }}
              >
                On-Time Rate
              </text>
            </RadialBarChart>
          </ResponsiveContainer>
        </Box>
      )}
      <Stack direction="row" justifyContent="center" spacing={3} sx={{ mt: 2 }}>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: COLORS.onTime }} />
          <Typography variant="caption" sx={{ color: "#64748B" }}>Target: 80%</Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: COLORS.primary }} />
          <Typography variant="caption" sx={{ color: "#64748B" }}>Actual</Typography>
        </Stack>
      </Stack>
    </Paper>
  );
};

// Interactive Donut Chart
const ResponseDonut = ({ data, loading, onFilterChange, activeStatus }) => {
  const [activeIndex, setActiveIndex] = useState(null);

  const chartData = [
    { name: "On Time", status: "ON_TIME", value: data?.metSla || 0, color: COLORS.onTime },
    { name: "Late", status: "LATE", value: data?.late || 0, color: COLORS.late },
    { name: "Missed", status: "NOT_CALLED", value: data?.missedSla || 0, color: COLORS.missed },
  ];

  const total = chartData.reduce((sum, item) => sum + item.value, 0);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 1,
        border: "1px solid #E2E8F0",
        height: "100%",
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#475569", mb: 2 }}>
        Response Distribution
      </Typography>
      {loading ? (
        <Skeleton variant="circular" width={140} height={140} sx={{ mx: "auto" }} />
      ) : (
        <>
          <Box sx={{ height: 160 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={85}
                  paddingAngle={4}
                  dataKey="value"
                  onMouseEnter={(_, index) => setActiveIndex(index)}
                  onMouseLeave={() => setActiveIndex(null)}
                  onClick={(e) => onFilterChange(e.status)}
                  style={{ cursor: "pointer" }}
                >
                  {chartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.color}
                      stroke={activeIndex === index || activeStatus === entry.status ? entry.color : "transparent"}
                      strokeWidth={activeIndex === index || activeStatus === entry.status ? 3 : 0}
                      style={{
                        transform: activeIndex === index || activeStatus === entry.status ? "scale(1.05)" : "scale(1)",
                        transformOrigin: "center",
                        transition: "all 0.2s ease",
                      }}
                    />
                  ))}
                </Pie>
                <RechartsTooltip
                  content={({ active, payload }) => {
                    if (active && payload?.length) {
                      const item = payload[0];
                      return (
                        <Box
                          sx={{
                            bgcolor: "#fff",
                            p: 1.5,
                            borderRadius: 1,
                            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                            border: `2px solid ${item.payload.color}`,
                          }}
                        >
                          <Typography variant="caption" sx={{ fontWeight: 700 }}>
                            {item.name}: {item.value}
                          </Typography>
                          <Typography variant="caption" sx={{ display: "block", color: "#64748B" }}>
                            {total > 0 ? ((item.value / total) * 100).toFixed(1) : 0}%
                          </Typography>
                        </Box>
                      );
                    }
                    return null;
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </Box>
          <Stack spacing={1.5} sx={{ mt: 2 }}>
            {chartData.map((item) => (
              <Stack
                key={item.name}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                onClick={() => onFilterChange(item.status)}
                sx={{
                  cursor: "pointer",
                  p: 0.5,
                  borderRadius: 1,
                  bgcolor: activeStatus === item.status ? `${item.color}10` : "transparent",
                  "&:hover": { bgcolor: `${item.color}10` },
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box sx={{ width: 10, height: 10, borderRadius: 1, bgcolor: item.color }} />
                  <Typography variant="caption" sx={{ fontWeight: activeStatus === item.status ? 700 : 600, color: "#475569" }}>
                    {item.name}
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "#1E293B" }}>
                    {item.value}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={total > 0 ? (item.value / total) * 100 : 0}
                    sx={{
                      width: 60,
                      height: 6,
                      borderRadius: 1,
                      bgcolor: "#F1F5F9",
                      "& .MuiLinearProgress-bar": { bgcolor: item.color, borderRadius: 1 },
                    }}
                  />
                </Stack>
              </Stack>
            ))}
          </Stack>
        </>
      )}
    </Paper>
  );
};

// Campaign Performance Chart
const CampaignChart = ({ data, loading, onFilterChange, activeCampaign }) => {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 1,
        border: "1px solid #E2E8F0",
        height: "100%",
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#475569" }}>
          Campaign Performance
        </Typography>
        <Stack direction="row" spacing={1}>
          {activeCampaign && (
            <Chip
              size="small"
              label={activeCampaign}
              onDelete={() => onFilterChange(null)}
              sx={{ bgcolor: `${COLORS.primary}15`, color: COLORS.primary, fontWeight: 700 }}
            />
          )}
          <Chip
            size="small"
            label={`${data?.length || 0} campaigns`}
            sx={{ bgcolor: "#F1F5F9", fontWeight: 600, fontSize: "0.7rem" }}
          />
        </Stack>
      </Stack>
      {loading ? (
        <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 1 }} />
      ) : (
        <Box sx={{ height: 220 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data || []}
              layout="vertical"
              margin={{ left: 20, right: 20 }}
              onClick={(e) => e && onFilterChange(e.activeLabel)}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E2E8F0" />
              <XAxis type="number" hide />
              <YAxis
                dataKey="name"
                type="category"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 11, fontWeight: 600, fill: "#64748B" }}
                width={100}
              />
              <RechartsTooltip
                cursor={{ fill: "#F8FAFC" }}
                contentStyle={{
                  borderRadius: 1,
                  border: "none",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                }}
              />
              <Bar dataKey="ON_TIME" name="On Time" fill={COLORS.onTime} stackId="a" style={{ cursor: "pointer" }} />
              <Bar dataKey="LATE" name="Late" fill={COLORS.late} stackId="a" style={{ cursor: "pointer" }} />
              <Bar dataKey="NOT_CALLED" name="Missed" fill={COLORS.missed} stackId="a" radius={[0, 4, 4, 0]} style={{ cursor: "pointer" }} />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Paper>
  );
};

// Hourly Trend Chart
const HourlyTrend = ({ data, loading }) => {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 1,
        border: "1px solid #E2E8F0",
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#475569", mb: 2 }}>
        Hourly Lead Volume
      </Typography>
      {loading ? (
        <Skeleton variant="rectangular" height={160} sx={{ borderRadius: 1 }} />
      ) : (
        <Box sx={{ height: 160 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data || []}>
              <defs>
                <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.2} />
                  <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorOnTime" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS.onTime} stopOpacity={0.2} />
                  <stop offset="95%" stopColor={COLORS.onTime} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
              <XAxis dataKey="time" fontSize={10} axisLine={false} tickLine={false} />
              <YAxis fontSize={10} axisLine={false} tickLine={false} />
              <RechartsTooltip
                contentStyle={{
                  borderRadius: 12,
                  border: "none",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                }}
              />
              <Area
                type="monotone"
                dataKey="total"
                stroke={COLORS.primary}
                fillOpacity={1}
                fill="url(#colorTotal)"
                strokeWidth={2}
                name="Total"
              />
              <Area
                type="monotone"
                dataKey="onTime"
                stroke={COLORS.onTime}
                fillOpacity={1}
                fill="url(#colorOnTime)"
                strokeWidth={2}
                name="On Time"
              />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Paper>
  );
};

// Main Component
const SlaReportMui = () => {
  const userId = localStorage.getItem("userId");
  const [selectedDateRange, setSelectedDateRange] = useState(null);
  const [calendarVisible, setCalendarVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [campaignFilter, setCampaignFilter] = useState(null);
  const [slaAnchorEl, setSlaAnchorEl] = useState(null);
  const [slaTimeRange, setSlaTimeRange] = useState({ label: "07:30 - 22:00", start: 450, end: 1320 });

  const SLA_PRESETS = [
    { label: "07:30 - 22:00", start: 450, end: 1320 },
    { label: "24 Hours", start: 0, end: 1440 },
  ];

  const [data, setData] = useState({
    details: [],
    summary: {},
    chartData: { pie: [], bar: [], trend: [] },
  });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = {
        startDate: selectedDateRange ? selectedDateRange[0].toISOString() : null,
        endDate: selectedDateRange ? selectedDateRange[1].toISOString() : null,
        slaStart: slaTimeRange.start,
        slaEnd: slaTimeRange.end,
      };
      const res = await axiosInstance.get(
        `${apiEndPointsConfig.getSlaReport}/me`,
        { params }
      );
      setData(res.data);
    } catch (err) {
      console.error("Error", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedDateRange, slaTimeRange]);

  // Handle Export
  const handleExport = () => {
    try {
      if (!filteredDetails.length) {
        alert("No data to export");
        return;
      }
      const exportData = filteredDetails.map((lead) => ({
        Lead: lead.name || "—",
        Campaign: lead.campaign,
        Phone: lead.phone,
        Date: new Date(lead.leadCreated).toLocaleDateString(),
        Time: new Date(lead.leadCreated).toLocaleTimeString(),
        Status: lead.status.replace("_", " "),
        "Response Minutes": lead.status === "NOT_CALLED" ? "—" : lead.mins,
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "SLA Report");
      XLSX.writeFile(wb, `SLA_Report_${new Date().toISOString().split("T")[0]}.xlsx`);
    } catch (error) {
      console.error("Export failed:", error);
      alert("Export failed. Please check console.");
    }
  };

  // Filter data by search, status, and campaign
  const filteredDetails = useMemo(() => {
    let result = [...data.details];

    // Status Filter
    if (statusFilter !== "ALL") {
      result = result.filter((l) => l.status === statusFilter);
    }

    // Campaign Filter
    if (campaignFilter) {
      result = result.filter((l) => l.campaign === campaignFilter);
    }

    // Search Filter
    if (searchQuery) {
      result = result.filter(
        (lead) =>
          lead.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          lead.phone?.includes(searchQuery) ||
          lead.campaign?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Sort by most recent (newest first)
    return result.sort((a, b) => new Date(b.leadCreated).getTime() - new Date(a.leadCreated).getTime());
  }, [data.details, searchQuery, statusFilter, campaignFilter]);

  const avgResponseTime = useMemo(() => {
    const leadsWithCalls = data.details.filter((l) => l.status !== "NOT_CALLED");
    if (!leadsWithCalls.length) return 0;
    const totalMins = leadsWithCalls.reduce((sum, l) => sum + (l.mins || 0), 0);
    return (totalMins / leadsWithCalls.length).toFixed(1);
  }, [data.details]);

  const paginatedData = filteredDetails.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const formatTime = (dateString) =>
    new Date(dateString).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
    });

  const getStatusConfig = (status) => {
    switch (status) {
      case "ON_TIME":
        return { icon: CheckCircle, color: COLORS.onTime, label: "On Time", bg: "#ECFDF5" };
      case "LATE":
        return { icon: AlertTriangle, color: COLORS.late, label: "Late", bg: "#FFFBEB" };
      default:
        return { icon: XCircle, color: COLORS.missed, label: "Missed", bg: "#FEF2F2" };
    }
  };

  const slaPercentage = data.summary?.totalLeads > 0
    ? Math.round((data.summary.metSla / data.summary.totalLeads) * 100)
    : 0;

  return (
    <Box
      sx={{
        ml: { xs: 0, md: "280px" },
        minHeight: "100vh",
      }}
    >
      <SideNavbar />
      {/* <Box sx={{ display: { xs: "none", md: "initial" } }}>
        <Header title="SLA Analytics" />
      </Box> */}
      <Box component="main" sx={{ p: { xs: 2, md: 1 } }}>


        <ThemeProvider theme={theme}>
          <CssBaseline />

          <Container maxWidth="xl" sx={{ py: 3 }}>
            {/* Header / Actions Row */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 800, color: "#1E293B" }}>
                  SLA Performance
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Lead response time and compliance metrics
                </Typography>
              </Box>
              <Stack direction="row" spacing={1.5}>
                {selectedDateRange && (
                  <Chip
                    label={`${selectedDateRange[0].toLocaleDateString("en-GB")} - ${selectedDateRange[1].toLocaleDateString("en-GB")}`}
                    onDelete={() => setSelectedDateRange(null)}
                    sx={{
                      bgcolor: "#EEF2FF",
                      color: COLORS.primary,
                      fontWeight: 600,
                      borderRadius: 1,
                    }}
                  />
                )}
                <Button
                  variant="outlined"
                  startIcon={<Download size={18} />}
                  onClick={handleExport}
                  sx={{
                    color: "#475569",
                    borderColor: "#E2E8F0",
                    fontWeight: 600,
                    borderRadius: 1,
                    textTransform: "none",
                    "&:hover": { bgcolor: "#F8FAFC", borderColor: "#CBD5E1" },
                  }}
                >
                  Export Excel
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<TimerIcon size={18} />}
                  endIcon={<ExpandMoreIcon size={18} />}
                  onClick={(e) => setSlaAnchorEl(e.currentTarget)}
                  sx={{
                    color: "#475569",
                    borderColor: "#E2E8F0",
                    fontWeight: 600,
                    borderRadius: 1,
                    textTransform: "none",
                    whiteSpace: "nowrap",
                    "&:hover": { bgcolor: "#F8FAFC", borderColor: "#CBD5E1" },
                  }}
                >
                  SLA: {slaTimeRange.label}
                </Button>

                <Menu
                  anchorEl={slaAnchorEl}
                  open={Boolean(slaAnchorEl)}
                  onClose={() => setSlaAnchorEl(null)}
                  PaperProps={{
                    sx: {
                      borderRadius: "12px",
                      mt: 1,
                      boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                      border: "1px solid #f1f5f9",
                    }
                  }}
                >
                  {SLA_PRESETS.map((preset) => (
                    <MenuItem
                      key={preset.label}
                      onClick={() => {
                        setSlaTimeRange(preset);
                        setSlaAnchorEl(null);
                        setPage(0);
                      }}
                      sx={{
                        fontSize: 13,
                        fontWeight: 500,
                        py: 1,
                        px: 2,
                        color: slaTimeRange.label === preset.label ? "#6366f1" : "#1e293b",
                        bgcolor: slaTimeRange.label === preset.label ? "#f5f3ff" : "transparent",
                        "&:hover": { bgcolor: "#f8fafc" }
                      }}
                    >
                      {preset.label}
                    </MenuItem>
                  ))}
                </Menu>

                <Button
                  variant="contained"
                  startIcon={<CalendarMonthIcon size={18} />}
                  onClick={() => setCalendarVisible(!calendarVisible)}
                  sx={{
                    bgcolor: COLORS.primary,
                    color: "#fff",
                    fontWeight: 600,
                    borderRadius: 1,
                    textTransform: "none",
                    whiteSpace: "nowrap",
                    "&:hover": { bgcolor: "#4F46E5" },
                  }}
                >
                  Filter Date
                </Button>

                {calendarVisible && (
                  <Portal>
                    <ClickAwayListener onClickAway={() => setCalendarVisible(false)}>
                      <Box
                        sx={{
                          position: "fixed",
                          top: "120px",
                          right: "40px",
                          zIndex: 1300,
                          boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
                          borderRadius: 1,
                          bgcolor: "#fff",
                          overflow: "hidden",
                        }}
                      >
                        <Calendar
                          selectRange
                          onChange={(r) => {
                            setSelectedDateRange(r);
                            setCalendarVisible(false);
                            setPage(0);
                          }}
                          value={selectedDateRange}
                          maxDate={new Date()}
                        />
                      </Box>
                    </ClickAwayListener>
                  </Portal>
                )}
              </Stack>
            </Stack>
            {/* KPI Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <KPICard
                  title="Total Leads"
                  value={data.summary?.totalLeads || 0}
                  subtitle="Leads received"
                  icon={Target}
                  color={COLORS.primary}
                  loading={loading}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <KPICard
                  title="SLA Reliability"
                  value={`${slaPercentage}%`}
                  subtitle="Leads met target"
                  icon={Zap}
                  color={COLORS.onTime}
                  loading={loading}
                  trend={slaPercentage > 80 ? "Premium" : "Average"}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <KPICard
                  title="Avg. Response"
                  value={`${avgResponseTime}m`}
                  subtitle="Time for first call"
                  icon={Clock}
                  color={COLORS.late}
                  loading={loading}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <KPICard
                  title="Missed Ratio"
                  value={`${data.summary?.totalLeads > 0 ? ((data.summary.missedSla / data.summary.totalLeads) * 100).toFixed(1) : 0}%`}
                  subtitle="No response leads"
                  icon={Phone}
                  color={COLORS.missed}
                  loading={loading}
                />
              </Grid>
            </Grid>

            {/* Charts Row */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={3}>
                <SLAGauge percentage={slaPercentage} loading={loading} />
              </Grid>
              <Grid item xs={12} md={3}>
                <ResponseDonut
                  data={data.summary}
                  loading={loading}
                  onFilterChange={setStatusFilter}
                  activeStatus={statusFilter}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CampaignChart
                  data={data.chartData?.bar}
                  loading={loading}
                  onFilterChange={setCampaignFilter}
                  activeCampaign={campaignFilter}
                />
              </Grid>
            </Grid>

            {/* Trend Chart */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12}>
                <HourlyTrend data={data.chartData?.trend} loading={loading} />
              </Grid>
            </Grid>

            {/* Table Section */}
            <Paper
              elevation={0}
              sx={{
                borderRadius: 1,
                border: "1px solid #E2E8F0",
                overflow: "hidden",
              }}
            >
              {/* Table Header */}
              <Box
                sx={{
                  p: 2,
                  borderBottom: "1px solid #E2E8F0",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#1E293B" }}>
                    Lead Activity
                  </Typography>
                  <Chip
                    size="small"
                    label={`${filteredDetails.length} records`}
                    sx={{ bgcolor: "#F1F5F9", fontWeight: 600 }}
                  />
                  {(statusFilter !== "ALL" || campaignFilter || searchQuery) && (
                    <Button
                      size="small"
                      startIcon={<FilterX size={14} />}
                      onClick={() => {
                        setStatusFilter("ALL");
                        setCampaignFilter(null);
                        setSearchQuery("");
                      }}
                      sx={{ textTransform: "none", color: COLORS.error, fontWeight: 700 }}
                    >
                      Clear Filters
                    </Button>
                  )}
                </Stack>
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                  {["ALL", "ON_TIME", "LATE", "NOT_CALLED"].map((s) => (
                    <Chip
                      key={s}
                      label={s.replace("_", " ")}
                      size="small"
                      onClick={() => setStatusFilter(s)}
                      sx={{
                        borderRadius: 1,
                        fontWeight: 700,
                        bgcolor: statusFilter === s ? COLORS.primary : "#F1F5F9",
                        color: statusFilter === s ? "#fff" : "#64748B",
                        "&:hover": { bgcolor: statusFilter === s ? COLORS.primary : "#E2E8F0" },
                      }}
                    />
                  ))}
                </Stack>
                <TextField
                  size="small"
                  placeholder="Search leads..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setPage(0);
                  }}
                  sx={{ width: { xs: "100%", sm: 240 } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search size={18} color="#94A3B8" />
                      </InputAdornment>
                    ),
                    sx: { borderRadius: 1, bgcolor: "#F8FAFC" },
                  }}
                />
              </Box>

              {/* Table */}
              {!loading && filteredDetails.length === 0 ? (
                <EmptyState
                  title="No SLA Data Found"
                  subtitle="No leads match your current filters. Try adjusting the date range or search criteria."
                  icon="data"
                  onRefresh={fetchData}
                />
              ) : (
                <>
                  <TableContainer sx={{ maxHeight: 450 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          {["Lead", "Campaign", "Phone", "Created", "Status", "Response Time", "Action"].map((h) => (
                            <TableCell
                              key={h}
                              sx={{
                                bgcolor: "#F8FAFC",
                                fontWeight: 700,
                                color: "#64748B",
                                fontSize: "0.75rem",
                                textTransform: "uppercase",
                                letterSpacing: 0.5,
                                py: 1.5,
                              }}
                            >
                              {h}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody component={AnimatePresence}>
                        {loading
                          ? [...Array(5)].map((_, i) => (
                            <TableRow key={i}>
                              <TableCell colSpan={7}>
                                <Skeleton height={50} />
                              </TableCell>
                            </TableRow>
                          ))
                          : filteredDetails.length === 0
                            ? (
                              <TableRow>
                                <TableCell colSpan={7} sx={{ py: 0, border: "none" }}>
                                  <EmptyState
                                    title="No SLA Data Found"
                                    subtitle="No leads match your current filters. Try adjusting the date range or search criteria."
                                    icon="data"
                                    onRefresh={fetchData}
                                  />
                                </TableCell>
                              </TableRow>
                            )
                            : paginatedData.map((lead, idx) => {
                              const statusConfig = getStatusConfig(lead.status);
                              const StatusIcon = statusConfig.icon;
                              return (
                                <TableRow
                                  key={`${lead.phone}-${lead.leadCreated}`}
                                  component={motion.tr}
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  exit={{ opacity: 0 }}
                                  layout
                                  hover
                                  sx={{
                                    "&:hover": { bgcolor: "#FAFAFA" },
                                    transition: "all 0.2s",
                                  }}
                                >
                                  <TableCell>
                                    <Stack direction="row" alignItems="center" spacing={1.5}>
                                      <Avatar
                                        sx={{
                                          width: 32,
                                          height: 32,
                                          fontSize: "0.75rem",
                                          bgcolor: "#EEF2FF",
                                          color: COLORS.primary,
                                          fontWeight: 700,
                                        }}
                                      >
                                        {lead.name?.[0]?.toUpperCase() || "?"}
                                      </Avatar>
                                      <Typography variant="body2" sx={{ fontWeight: 600, color: "#1E293B" }}>
                                        {lead.name || "—"}
                                      </Typography>
                                    </Stack>
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      size="small"
                                      label={lead.campaign}
                                      sx={{ bgcolor: "#F1F5F9", fontWeight: 500, fontSize: "0.7rem" }}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: "#475569" }}>
                                      {lead.phone}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Stack>
                                      <Typography variant="caption" sx={{ fontWeight: 600, color: "#1E293B" }}>
                                        {formatDate(lead.leadCreated)}
                                      </Typography>
                                      <Typography variant="caption" sx={{ color: "#94A3B8" }}>
                                        {formatTime(lead.leadCreated)}
                                      </Typography>
                                    </Stack>
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      size="small"
                                      icon={<StatusIcon size={12} />}
                                      label={statusConfig.label}
                                      sx={{
                                        bgcolor: statusConfig.bg,
                                        color: statusConfig.color,
                                        fontWeight: 700,
                                        fontSize: "0.7rem",
                                        "& .MuiChip-icon": { color: statusConfig.color },
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 600,
                                        color: lead.status === "NOT_CALLED" ? "#94A3B8" : "#475569",
                                      }}
                                    >
                                      {lead.status === "NOT_CALLED" ? "—" : `${lead.mins} min`}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Tooltip title={lead.recordingUrl ? "Listen to Recording" : "No Recording"}>
                                      <span>
                                        <IconButton
                                          size="small"
                                          disabled={!lead.recordingUrl}
                                          onClick={() => window.open(lead.recordingUrl, "_blank")}
                                          sx={{
                                            bgcolor: lead.recordingUrl ? COLORS.primary : "#F1F5F9",
                                            color: lead.recordingUrl ? "#fff" : "#94A3B8",
                                            "&:hover": { bgcolor: lead.recordingUrl ? "#4F46E5" : "#F1F5F9" },
                                          }}
                                        >
                                          <PlayCircle size={16} />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Pagination */}
                  <TablePagination
                    component="div"
                    count={filteredDetails.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    onRowsPerPageChange={(e) => {
                      setRowsPerPage(parseInt(e.target.value, 10));
                      setPage(0);
                    }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    sx={{
                      borderTop: "1px solid #E2E8F0",
                      "& .MuiTablePagination-select": { fontWeight: 600 },
                    }}
                  />
                </>
              )}
            </Paper>
          </Container>
        </ThemeProvider>
      </Box>
    </Box>
  );
};

export default SlaReportMui;
